import { useCallback, useMemo } from 'react';
import { useParams, usePathname } from 'next/navigation';

import { SparksAgentService } from '@/components/composite/SparksAgent/services/SparksAgentService';
import { useSparksAgentContextStore } from '@/utils/zustand/stores/sparks-agent-context-store';

import useSocialProfiles from '@/utils/hooks/swr/useSocialProfiles';
import useActiveGroup from '@/utils/hooks/user/useActiveGroup';
import useActiveSocialProfile from '@/utils/hooks/user/useActiveSocialProfile';

import { getIsHeaderDatepickerGlobalDisabled } from '@/utils/layout/getIsHeaderDatepickerGlobalDisabled';
import { useDatepickerGlobalStore } from '@/utils/zustand/providers/datepicker-global-provider';
import { useProfilesStore } from '@/utils/zustand/providers/profiles-provider';

export function useSparksAgentRequestContext() {
    const pathname = usePathname() ?? '';
    const params = useParams();

    const datepickerDate = useDatepickerGlobalStore((s) => s.date);
    const datepickerPeriod = useDatepickerGlobalStore((s) => s.period);
    const datepickerCompareTo = useDatepickerGlobalStore((s) => s.compareTo);

    const { activatedSocialProfiles } = useSocialProfiles({ revalidateIfStale: false });
    const { activeGroup } = useActiveGroup();
    const { activeSocialProfile } = useActiveSocialProfile();
    const activeSocialProfileId = useProfilesStore((s) => s.activeSocialProfileId);
    const activeGroupId = useProfilesStore((s) => s.activeGroupId);
    const activeMultiSelectionProfiles = useProfilesStore((s) => s.activeMultiSelectionProfiles);

    const analyticsFields = useSparksAgentContextStore((s) => s.analyticsFields);
    const contextSocialProfiles = useSparksAgentContextStore((s) => s.contextSocialProfiles);

    const socialProfilesContextStrategy = useMemo(
        () => SparksAgentService.resolveSparksSocialProfilesContextStrategy(pathname),
        [pathname]
    );

    const isDatepickerPeriodContextApplicable = useMemo(
        () => !getIsHeaderDatepickerGlobalDisabled({ pathname, params }),
        [pathname, params]
    );

    const socialProfilesForContext = useMemo(() => {
        if (socialProfilesContextStrategy === 'omit') {
            return [];
        }
        if (socialProfilesContextStrategy === 'dashboard') {
            return contextSocialProfiles ?? [];
        }
        return SparksAgentService.resolveAnalyticsScopeSocialProfiles({
            activatedSocialProfiles,
            activeSocialProfile,
            profilesStore: {
                activeSocialProfileId,
                activeGroupId,
                activeMultiSelectionProfiles
            },
            activeGroup
        });
    }, [
        socialProfilesContextStrategy,
        contextSocialProfiles,
        activatedSocialProfiles,
        activeSocialProfile,
        activeSocialProfileId,
        activeGroupId,
        activeMultiSelectionProfiles,
        activeGroup
    ]);

    const getContext = useCallback(() => {
        return SparksAgentService.buildSparksAgentReadyContext({
            datepicker: {
                date: datepickerDate,
                period: datepickerPeriod,
                compareTo: datepickerCompareTo
            },
            socialProfiles: socialProfilesForContext,
            analyticsFields,
            includePeriodContext: isDatepickerPeriodContextApplicable
        });
    }, [
        analyticsFields,
        datepickerCompareTo,
        datepickerDate,
        datepickerPeriod,
        isDatepickerPeriodContextApplicable,
        socialProfilesForContext
    ]);

    return {
        getContext
    };
}
